import { ArrowRight } from 'assets/icons';
import CountBadge from 'components/badge/CountBadge';
import useSlideOver from 'components/SlideOver/useSlideOver';
import { addDays, clockLabel, isoDay, shortDayLabel } from 'helper/timeLabels';
import useHomeText from 'hooks/useHomeText';
import useMentions from 'hooks/useMentions';
import useTaskPanels from 'hooks/useTaskPanels';
import { CARD_CLASS } from 'utils/surfaceClasses';
import MentionItem, { MentionSkeleton } from './MentionItem';
import { MENTIONS_PANEL } from './MentionsPanel';

// The latest mentions the card shows: « Voir tout » opens the others.
const SHOWN = 3;

// « 10:12 » today, « Hier », then « 20 oct. ».
const whenOf = (createdAt, now, yesterday) => {
  const at = new Date(createdAt);
  const day = isoDay(at);
  if (day === isoDay(now)) return clockLabel(at);
  if (day === isoDay(addDays(now, -1))) return yesterday;
  return shortDayLabel(day);
};

/**
 * « Mentions » on the home page: the latest comments of the tasks that name
 * the user (@Prénom Nom), the unread ones counted. One opens its task on its
 * comments, which reads it; « Voir tout » opens all of them in a panel.
 */
const MentionsCard = () => {
  const { text } = useHomeText();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const { mentions, failed, me } = useMentions();

  const now = new Date();
  const unread = (mentions ?? []).filter((mention) => mention.unread).length;

  return (
    <section
      className={`flex shrink-0 flex-col gap-2 px-4 pb-3 pt-3 ${CARD_CLASS}`}
    >
      <div className="flex items-center gap-2">
        <p className="text-base font-semibold text-gray-900">
          {text('mentions.title')}
        </p>
        {unread > 0 && <CountBadge value={unread} />}
        {mentions?.length > 0 && (
          <button
            type="button"
            onClick={() => toggleSlideOver(MENTIONS_PANEL)}
            className="ml-auto flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-gray-900"
          >
            {text('mentions.seeAll')}
            <ArrowRight wh={14} color="currentColor" />
          </button>
        )}
      </div>
      {failed && (
        <p className="text-sm text-gray-400">{text('mentions.loadError')}</p>
      )}
      {!failed && !mentions && <MentionSkeleton />}
      {!failed && mentions?.length === 0 && (
        <p className="text-sm text-gray-400">{text('mentions.empty')}</p>
      )}
      {!failed && mentions?.length > 0 && (
        <ul className="-mx-2 flex flex-col">
          {mentions.slice(0, SHOWN).map((mention) => (
            <li key={mention.id}>
              <MentionItem
                mention={mention}
                me={me}
                when={whenOf(mention.createdAt, now, text('tasks.yesterday'))}
                onOpen={() => openTask(mention.task.id, { tab: 'comments' })}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default MentionsCard;
